import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useToast } from "@/components/ui/use-toast";

const PrivacySettings = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  
  // Sample privacy settings - would be fetched from API in a real app
  const [profileVisibility, setProfileVisibility] = useState<string>("public");
  const [showEmail, setShowEmail] = useState<boolean>(false);
  const [showLocation, setShowLocation] = useState<boolean>(true);
  const [allowDataUsage, setAllowDataUsage] = useState<boolean>(true);
  const [allowDataSharing, setAllowDataSharing] = useState<boolean>(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      // Implement privacy settings update logic here
      
      toast({
        title: "Privacy settings updated",
        description: "Your privacy settings have been saved.",
      });
    } catch (error) {
      toast({
        title: "Error updating privacy settings",
        description: "There was a problem saving your privacy settings.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="space-y-4">
        <h3 className="text-lg font-medium">Profile Privacy</h3>
        
        <div className="space-y-2">
          <Label htmlFor="profile-visibility">Profile Visibility</Label>
          <RadioGroup 
            id="profile-visibility" 
            value={profileVisibility} 
            onValueChange={setProfileVisibility}
            className="mt-2"
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="public" id="public" />
              <Label htmlFor="public" className="cursor-pointer">
                Public - Anyone can view your profile
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="registered" id="registered" />
              <Label htmlFor="registered" className="cursor-pointer">
                Registered Users Only - Only registered users can view your profile
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="private" id="private" />
              <Label htmlFor="private" className="cursor-pointer">
                Private - Only you and platform administrators can view your profile
              </Label>
            </div>
          </RadioGroup>
        </div>
        
        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <Label htmlFor="show-email" className="text-base">
              Show Email Address
            </Label>
            <p className="text-sm text-muted-foreground">
              Allow other users to see your email address on your profile
            </p>
          </div>
          <Switch
            id="show-email"
            checked={showEmail}
            onCheckedChange={setShowEmail}
          />
        </div>
        
        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <Label htmlFor="show-location" className="text-base">
              Show Location
            </Label>
            <p className="text-sm text-muted-foreground">
              Show your general location on your profile
            </p>
          </div>
          <Switch
            id="show-location"
            checked={showLocation}
            onCheckedChange={setShowLocation}
          />
        </div>
      </div>
      
      <div className="space-y-4">
        <h3 className="text-lg font-medium">Data Usage</h3>
        
        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <Label htmlFor="allow-data-usage" className="text-base">
              Usage Analytics
            </Label>
            <p className="text-sm text-muted-foreground">
              Allow us to collect anonymous usage data to improve the platform
            </p>
          </div>
          <Switch
            id="allow-data-usage"
            checked={allowDataUsage}
            onCheckedChange={setAllowDataUsage}
          />
        </div>
        
        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <Label htmlFor="allow-data-sharing" className="text-base">
              Data Sharing
            </Label>
            <p className="text-sm text-muted-foreground">
              Allow us to share anonymous data with third-party partners
            </p>
          </div>
          <Switch
            id="allow-data-sharing"
            checked={allowDataSharing}
            onCheckedChange={setAllowDataSharing}
          />
        </div>
      </div>
      
      <Button type="submit" disabled={loading}>
        {loading ? "Saving..." : "Save Privacy Settings"}
      </Button>
      
      <div className="pt-4 border-t border-gray-200 dark:border-gray-800">
        <h3 className="text-lg font-medium text-red-600 dark:text-red-400 mb-2">
          Danger Zone
        </h3>
        <p className="text-sm text-muted-foreground mb-4">
          Once you delete your account, there is no going back. This action is permanent.
        </p>
        <Button variant="destructive">Delete Account</Button>
      </div>
    </form>
  );
};

export default PrivacySettings; 